import { Allow, parse } from 'partial-json';

import type { PublicPluginAPI } from '@atlaskit/editor-common/types';

import { ADFStreamer } from '@atlassian/ai-model-io/adf-streamer';
import type {
	AiStreamingOrchestratorPlugin,
	StreamingCommand,
} from '@atlassian/editor-plugin-ai-streaming-orchestrator';
import type { AIPlugin } from '@atlassian/editor-plugin-ai';
import { fg } from '@confluence/feature-gating';

import { log } from '../utils/aifc-logging';

import {
	FinishAction,
	InitAction,
	PartialStreamParser,
	StreamAction,
} from './partial-stream-parser';
import { AdfParser } from './adf-parser';
import { DocNode } from '@atlaskit/adf-schema';

const requiresNodeId = (action: string) => ACTIONS_REQUIRING_NODE_ID.has(action);
const ACTIONS_REQUIRING_NODE_ID = new Set([
	'replaceNode',
	'insertNodeBefore',
	'insertNodeAfter',
	'removeNode',
	'wrap',
]);

export class ConfluencePageCreateCommandProcessor {
	private editorApi: PublicPluginAPI<[AIPlugin, AiStreamingOrchestratorPlugin]>;
	private actionId: string | undefined;
	private prevAdf: string = '';
	private accumulated: string = '';
	private streamer: ADFStreamer | undefined;

	constructor(editorApi: PublicPluginAPI<[AIPlugin, AiStreamingOrchestratorPlugin]>) {
		this.editorApi = editorApi;
	}

	private init() {
		if (fg('platform_editor_free_gen_session')) {
			this.editorApi.aiStreamingOrchestrator?.actions.startSession();
		}

		this.actionId = this.editorApi.aiStreamingOrchestrator?.actions.initAction({
			type: 'appendNode',
			streamingType: 'create',
		});
		if (!this.actionId) {
			throw new Error('Failed to initialize streaming action for create');
		}

		const schema = this.editorApi.core.sharedState.currentState()?.schema;
		if (schema) {
			this.streamer = new ADFStreamer(schema);
		}

		log('Initialized editor streaming action', { streamType: 'create', actionId: this.actionId });
	}

	stream(adf: string) {
		if (!this.actionId) {
			this.init();
		}

		if (!adf) {
			return;
		}

		const nextChunk = adf.substring(this.prevAdf.length);
		if (this.streamer) {
			this.streamer.processChunk(nextChunk);
		}
		this.editorApi.aiStreamingOrchestrator?.actions.streamValue(this.actionId!, nextChunk);
		this.prevAdf = adf;
		this.accumulated += nextChunk;
		log('Streaming content', { actionId: this.actionId, adf: nextChunk });
	}

	async finish() {
		if (this.actionId) {
			if (fg('platform_editor_free_gen_session')) {
				// eslint-disable-next-line @typescript-eslint/no-floating-promises -- this is on purpose, we are going to make it synchronous
				this.editorApi?.aiStreamingOrchestrator?.actions.finishAction(this.actionId);
				await this.editorApi?.aiStreamingOrchestrator?.actions.endSession();
			} else {
				await this.editorApi?.aiStreamingOrchestrator?.actions.finishAction(this.actionId);
			}
			log('Finished action', { actionId: this.actionId, fullAdf: this.accumulated });

			let finalDoc: DocNode | undefined;
			if (fg('confluence-aifc-custom-adf-parser')) {
				try {
					const parser = new AdfParser(this.accumulated);
					const doc = parser.parse();
					const schema = this.editorApi.core.sharedState.currentState()?.schema;
					if (!schema) {
						throw new Error('Schema is not available');
					}

					const newStreamer = new ADFStreamer(schema);
					newStreamer.processChunk(JSON.stringify(doc || {}));
					finalDoc = newStreamer.getLastDoc();
				} catch (error) {
					log('Customer Parse failed, using default parser', { error: error });
					if (this.streamer) {
						finalDoc = this.streamer.getLastDoc();
					}
				}
			} else if (this.streamer) {
				finalDoc = this.streamer.getLastDoc();
			}

			this.accumulated = '';
			this.actionId = undefined;
			this.prevAdf = '';

			return finalDoc;
		}
	}

	abort() {
		this.editorApi.aiStreamingOrchestrator?.actions.abortAll();
	}
}

class ToolCallProcessor {
	private editorApi: PublicPluginAPI<[AIPlugin, AiStreamingOrchestratorPlugin]>;
	private actionId: string;
	private accumulated: string = '';

	constructor(
		editorApi: PublicPluginAPI<[AIPlugin, AiStreamingOrchestratorPlugin]>,
		command: StreamingCommand,
	) {
		this.editorApi = editorApi;
		const actionId = this.editorApi?.aiStreamingOrchestrator?.actions.initAction(command);
		if (!actionId) {
			throw new Error('Failed to initialize streaming action for update');
		}
		this.actionId = actionId;
		log('Initialized editor streaming action', { streamType: 'update', actionId, command });
	}

	stream(adf: string) {
		if (!adf) {
			return;
		}

		this.accumulated += adf;
		this.editorApi?.aiStreamingOrchestrator?.actions.streamValue(this.actionId, adf);
		log('Streaming content', { actionId: this.actionId, adf });
	}

	async finish() {
		await this.editorApi?.aiStreamingOrchestrator?.actions.finishAction(this.actionId);
		log('Finished action', { actionId: this.actionId, fullAdf: this.accumulated });
		this.accumulated = '';
	}
}

export class ConfluencePageUpdateCommandProcessor {
	private editorApi: PublicPluginAPI<[AIPlugin, AiStreamingOrchestratorPlugin]>;
	private currentToolCallIndex: number | undefined;
	private accumulated: string = '';

	constructor(editorApi: PublicPluginAPI<[AIPlugin, AiStreamingOrchestratorPlugin]>) {
		this.editorApi = editorApi;
	}

	private async parseToolCallAndStream(toolCall: any) {
		const value = toolCall?.arguments?.value;
		const action = toolCall?.name;

		let localId: string | undefined;
		if (requiresNodeId(action)) {
			localId = toolCall?.arguments?.localId;
			if (!localId) {
				return;
			}
		}

		let command: StreamingCommand;
		switch (action) {
			case 'replaceNode':
			case 'insertNodeBefore':
			case 'insertNodeAfter':
			case 'removeNode':
				command = {
					type: action,
					localId,
				};
				break;
			case 'appendNode':
				command = {
					type: action,
				};
				break;
			case 'wrap':
				const wrapperType = toolCall?.arguments?.wrapperType;
				const wrapperAttrs = toolCall?.arguments?.wrapperAttrs;
				if (!wrapperType || !wrapperAttrs) {
					return;
				}

				command = {
					type: action,
					localId,
					wrapperType,
					wrapperAttrs: JSON.parse(wrapperAttrs),
				};
				break;
			default:
				return;
		}

		const toolCallProcessor = new ToolCallProcessor(this.editorApi, command);
		toolCallProcessor.stream(value);
		if (fg('platform_editor_free_gen_session')) {
			// eslint-disable-next-line @typescript-eslint/no-floating-promises -- this is on purpose, we are going to make it synchronous
			toolCallProcessor.finish();
		} else {
			await toolCallProcessor.finish();
		}
	}

	streamChunk(chunk: string, isFinished: boolean) {
		if (this.accumulated.length === 0 && fg('platform_editor_free_gen_session')) {
			this.editorApi.aiStreamingOrchestrator?.actions.startSession();
		}

		this.accumulated += chunk;
		return this.stream(this.accumulated, isFinished);
	}

	private async stream(document: string, isFinished: boolean) {
		let toolCalls: any[];

		try {
			const result = parse(document, Allow.ALL) ?? {};
			// When chunked, the tool calls are nested in the data property
			toolCalls = result?.data ?? [];
		} catch {
			return;
		}

		if (this.currentToolCallIndex === undefined) {
			if (toolCalls.length > 0) {
				this.currentToolCallIndex = 0;
			} else {
				// In case the parsed tool calls are empty
				return;
			}
		}

		// Unless we know we have the last message, only process up to the
		// penultimate tool call so that we can guarantee the data is complete.
		const endIndex = isFinished ? toolCalls.length : toolCalls.length - 1;

		for (; this.currentToolCallIndex < endIndex; this.currentToolCallIndex++) {
			await this.parseToolCallAndStream(toolCalls[this.currentToolCallIndex]);
		}
	}

	async finish() {
		this.currentToolCallIndex = undefined;
		this.accumulated = '';
		if (fg('platform_editor_free_gen_session')) {
			await this.editorApi.aiStreamingOrchestrator?.actions.endSession();
		}
	}

	abort() {
		this.editorApi.aiStreamingOrchestrator?.actions.abortAll();
	}
}

export class ConfluencePageUpdateCommandProcessorWithPartialStreaming {
	private editorApi: PublicPluginAPI<[AIPlugin, AiStreamingOrchestratorPlugin]>;
	private parser: PartialStreamParser;
	private currentToolCall: ToolCallProcessor | undefined;
	private accumulated: string = '';

	constructor(editorApi: PublicPluginAPI<[AIPlugin, AiStreamingOrchestratorPlugin]>) {
		this.editorApi = editorApi;
		this.parser = new PartialStreamParser();
	}

	private createToolCallProcessor(toolCall: InitAction) {
		const type = toolCall.name;
		const localId = toolCall.localId;

		let command: StreamingCommand;
		switch (type) {
			case 'replaceNode':
			case 'insertNodeBefore':
			case 'insertNodeAfter':
			case 'removeNode':
				command = {
					type,
					localId: localId!,
				};
				break;
			case 'appendNode':
				command = {
					type,
				};
				break;
			case 'wrap':
				const wrapperType = toolCall.wrapperType;
				const wrapperAttrs = toolCall.wrapperAttrs;
				if (!wrapperType || !wrapperAttrs) {
					return;
				}

				command = {
					type,
					localId: localId!,
					wrapperType,
					wrapperAttrs,
				};
				break;
			default:
				return;
		}

		return new ToolCallProcessor(this.editorApi, command);
	}

	streamChunk(chunk: string, isFinished: boolean) {
		// The last message contains a copy of the full, accumulated tool call structure. We
		// don't want to reprocess this, so we just return instead.
		if (isFinished) {
			return;
		}

		if (this.accumulated.length === 0 && fg('platform_editor_free_gen_session')) {
			this.editorApi.aiStreamingOrchestrator?.actions.startSession();
		}

		this.accumulated += chunk;
		return this.stream(chunk);
	}

	private async stream(chunk: string) {
		try {
			const results = this.parser.parse(chunk);

			for (const result of results) {
				if (result instanceof InitAction) {
					this.currentToolCall = this.createToolCallProcessor(result);
				} else if (result instanceof StreamAction) {
					this.currentToolCall?.stream(result.partialValue);
				} else if (result instanceof FinishAction) {
					if (fg('platform_editor_free_gen_session')) {
						// eslint-disable-next-line @typescript-eslint/no-floating-promises -- this is on purpose, we are going to make it synchronous
						this.currentToolCall?.finish();
					} else {
						await this.currentToolCall?.finish();
					}
				} else {
					// eslint-disable-next-line no-console
					console.error('[debug]: Unknown action type', result);
				}
			}
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('[debug]: Error while parsing', error);
		}
	}

	async finish() {
		this.accumulated = '';
		if (fg('platform_editor_free_gen_session')) {
			await this.editorApi.aiStreamingOrchestrator?.actions.endSession();
		} else {
			await this.currentToolCall?.finish();
		}
		this.currentToolCall = undefined;
		this.parser.reset();
	}

	abort() {
		this.editorApi.aiStreamingOrchestrator?.actions.abortAll();
	}
}
